import { useNavigate, useLocation } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import useDashboardStats from "../../hooks/useDashboardStats";
import useLinkedUploadAgencies from "../../hooks/useLinkedUploadAgencies";
import DashboardStatsBar from "../dashboard/DashboardStatsBar";
import { withReturnPath } from "../../utils/navReturn";
import "../../styles/dashboard.css";

function formatCount(n) {
  const num = Number(n) || 0;
  return num.toLocaleString("en-IN");
}

export default function FindVehiclesStatsPanel({
  className = "fv-stats-panel home-field-stats",
  returnPathFallback = "/find-vehicles",
}) {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isRepoAdmin = auth?.user?.role === "REPO_ADMIN";
  const { stats, loading, error } = useDashboardStats();
  const {
    companyTotalRecords,
    allBanksNbfcsRecords,
    loading: sourcesLoading,
    error: sourcesError,
    reload: reloadSources,
  } = useLinkedUploadAgencies();

  const handleStatClick = (key) => {
    const returnTo =
      `${location.pathname}${location.search}` || returnPathFallback;

    if (key === "pending") {
      navigate(withReturnPath("/confirmation?status=PENDING", returnTo));
      return;
    }

    if (key === "confirmations") {
      navigate(withReturnPath("/confirmation?status=CONFIRMED", returnTo));
      return;
    }

    if (key === "inventory") {
      navigate(
        withReturnPath(
          "/confirmation?status=CONFIRMED&view=inventory-confirmed",
          returnTo
        )
      );
      return;
    }

    if (key === "records") {
      if (isRepoAdmin) {
        navigate(withReturnPath("/bank-details", returnTo));
      } else {
        navigate("/find-vehicles");
      }
    }
  };

  const handleCompanyTotalClick = () => {
    const returnTo =
      `${location.pathname}${location.search}` || returnPathFallback;
    if (isRepoAdmin) {
      navigate(withReturnPath("/bank-details", returnTo));
      return;
    }
    navigate("/find-vehicles");
  };

  const handleBanksClick = () => {
    const returnTo =
      `${location.pathname}${location.search}` || returnPathFallback;
    if (isRepoAdmin) {
      navigate(withReturnPath("/bank-records", returnTo));
      return;
    }
    handleStatClick("records");
  };

  const renderUploadSourceCards = () => {
    if (sourcesLoading) {
      return (
        <div className="fv-agency-upload-grid fv-agency-upload-grid--two" aria-busy="true" aria-label="Loading upload sources">
          {[0, 1].map((i) => (
            <div key={i} className="fv-agency-upload-card fv-agency-upload-card--skeleton">
              <div className="fv-agency-upload-card__value-skel" />
              <div className="fv-agency-upload-card__label-skel" />
            </div>
          ))}
        </div>
      );
    }

    if (sourcesError) {
      return (
        <div className="fv-agency-upload-empty fv-agency-upload-empty--error">
          <p className="error-text">{sourcesError}</p>
          <button type="button" className="secondary-page-btn" onClick={reloadSources}>
            Retry
          </button>
        </div>
      );
    }

    return (
      <div
        className="fv-agency-upload-grid fv-agency-upload-grid--two"
        role="list"
        aria-label="Upload record summary"
      >
        <button
          type="button"
          role="listitem"
          className="fv-agency-upload-card home-field-uploaded-card stat stat--clickable"
          onClick={handleCompanyTotalClick}
          title="Total Records (total records)"
        >
          <div className="stat-value">{formatCount(companyTotalRecords)}</div>
          <div className="stat-label">Total Records</div>
        </button>

        <button
          type="button"
          role="listitem"
          className="fv-agency-upload-card home-field-uploaded-card stat stat--clickable"
          onClick={handleBanksClick}
          title="All Banks/NBFC Uploaded Records"
        >
          <div className="stat-value">{formatCount(allBanksNbfcsRecords)}</div>
          <div className="stat-label">All Banks/NBFC Uploaded Records</div>
        </button>
      </div>
    );
  };

  return (
    <section
      className={className}
      aria-label="Upload and confirmation stats"
    >
      {error ? <p className="error-text fv-stats-error">{error}</p> : null}

      {renderUploadSourceCards()}

      <DashboardStatsBar
        className="home-hero__stats"
        variant="field"
        stats={stats}
        loading={loading}
        activeKey=""
        onStatClick={handleStatClick}
      />
    </section>
  );
}
